import { scaleBand, scaleOrdinal, scaleLinear } from 'd3-scale'
import { axisBottom } from 'd3-axis'
import { select } from 'd3-selection'
import { keys } from 'd3-collection'
import textures from 'textures'

import { emptyElement } from '../utils'

export const topSectionHeight = 350

const decilePostscript = {
  1: 'st',
  2: 'nd',
  3: 'rd',
  4: 'th',
  5: 'th',
  6: 'th',
  7: 'th',
  8: 'th',
  9: 'th',
  10: 'th',
}

export function contextOverview(data, getDIData, isDecile) {
  /*1-10 = 1st
11-20 = 2nd
21-30= 3rd
31-40 = 4th
41-50 = 5th
51-60 = 6th
61-70 = 7th
71-80= 8th
81-90 = 9th
91-100 = 10th */

  let levelMin = {
    data: [
      {
        color: '#3582D4',
        color2: '#246298',
        label: 'Neighborhood Average',
        tip: 'Average of the Overall disadvantage levels for enrolled students at your institution.',
        value2: +getDIData.di_nh_avg,
        value: +data.a_nh_overrall_ptile,
        group: 1,
      },
      {
        color: '#246298',
        color2: '#3582D4',
        label: 'High School Average',
        tip: 'Average of the Overall disadvantage levels for enrolled students at your institution.',
        value2: +data.a_hs_overrall_ptile,
        value: +getDIData.di_hs_avg,
        group: 2,
      },
    ],
  }

  const container = document.getElementById('overallLevelSummaryEnvironmentalContext')
  emptyElement(container)

  let margin = {
    top: 50,
    right: 50,
    bottom: 40,
    left: 40,
  }

  let width = container.clientWidth - margin.left - margin.right
  let height = topSectionHeight - margin.top - margin.bottom
  let x0 = scaleBand().rangeRound([0, width]).padding(0.2)

  let x1 = scaleBand() // scaleOrdinal()

  let color = scaleOrdinal().range(['rgb(113, 197, 232)', 'rgb(0, 98, 151)'])

  let xAxis = axisBottom(x0).tickSize(0) //vital for setting axis

  // different yScale functions b/c decile version max value is 10
  // not 100
  let yScaleDecile = scaleLinear().range([height, 0]).domain([0, 10])
  let yScale = scaleLinear().range([height, 0]).domain([0, 100])

  const switchToDecile = (value) => {
    if (!value) {
      return 'NA'
    }
    const decileVal = getDecileValue(value)
    return `${decileVal}${decilePostscript[decileVal]}`
  }

  const getDecileValue = (value) => {
    if (!value) {
      return 0
    }
    return Math.floor((value - 1) / 10) + 1
  }

  const getValue = (value, decile) => {
    if (!decile) {
      return yScale(value)
    }

    return yScaleDecile(getDecileValue(value))
  }

  let tip = select('#overallLevelSummaryEnvironmentalContext')
    .append('div')
    .attr('class', 'tooltip top cb-axis-tooltip')
    .attr('role', 'tooltip')
    .attr('aria-hidden', 'true')
    .style('opacity', '0')
    .style('display', 'none')

  const texture = textures.lines().thicker().stroke('#3582D4')

  let svg = select('#overallLevelSummaryEnvironmentalContext')
    .append('svg')
    .attr('class', isDecile ? 'decile chart' : 'chart')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .attr('aria-hidden', 'true')
    .append('g')
    .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')')

  svg.call(texture)

  let groupNames = keys(levelMin.data[0]).filter(function (key) {
    return (
      key !== 'label' && key !== 'group' && key !== 'tip' && key !== 'color' && key !== 'color2'
    )
  })
  levelMin.data.forEach(function (d) {
    d.group = groupNames.map(function (name) {
      return { name: name, value: +d[name], group: d.group }
    })
  })
  let color2 = scaleOrdinal().range(['#246298', '#246298'])

  x0.domain(
    levelMin.data.map(function (d) {
      return d.label
    })
  )
  x1.domain(groupNames).rangeRound([0, x0.bandwidth()])

  svg
    .append('g')
    .attr('class', 'x axis')
    .attr('transform', 'translate(0,' + height + ')')
    .attr('aria-hidden', 'true')
    .call(xAxis)
    .selectAll('.tick text')
    .call(wrap, x0.bandwidth() / 2)
    .attr('transform', function (d) {
      if (d === 'Neighborhood Average') {
        return 'translate(' + x1.bandwidth() / 1.6 + ',0)'
      } else if (d === 'High School Average') {
        return 'translate(' + -x1.bandwidth() / 1.4 + ',0)'
      }
    })

  let label = svg
    .selectAll('.label')
    .data(levelMin.data)
    .enter()
    .append('g')
    .attr('aria-hidden', 'true')
    .attr('class', 'g')
    .attr('transform', function (d) {
      return 'translate(' + x0(d.label) + ',0)'
    })

  label
    .selectAll('bar') //determines placement of bars
    .data(function (d) {
      //picks which values to show
      return d.group
    })
    .enter()
    .append('rect') //determines placement of bars
    .attr('width', function (d) {
      if (d.value == data.a_hs_overrall_ptile && d.name === 'value2' && d.group === 2) {
        return x1.bandwidth()
      } else if (d.value == data.a_nh_overrall_ptile && d.name === 'value' && d.group === 1) {
        return x1.bandwidth()
      } else {
        return x1.bandwidth() / 3
      }
    })
    .attr('transform', function (d) {
      if (d.value == getDIData.di_nh_avg && d.name === 'value2' && d.group === 1) {
        return 'translate(' + x1.bandwidth() * 0.66 + ',0)'
      } else if (isNaN(d.value)) {
        return 'translate(0,0)'
      }
    }) //determines width of bars
    .attr('x', function (d) {
      //displays two bars that are in the right area
      return x1(d.name)
    })
    .attr('class', function (d) {
      if (d.value == data.a_hs_overrall_ptile && d.name === 'value2' && d.group === 2) {
        return 'value'
      } else if (d.value == data.a_nh_overrall_ptile && d.name === 'value' && d.group === 1) {
        return 'value'
      } else {
        return 'value2'
      }
    })
    .attr('y', function (d) {
      // flips it so it is on the axis
      return getValue(d.value, isDecile) || 0
    })
    .attr('height', function (d) {
      //draws the actual bar
      return Math.max(height - (getValue(d.value, isDecile) || 0), 0)
    })
    .attr('aria-hidden', 'true')
    .style('fill', function (d) {
      if (d.value == data.a_hs_overrall_ptile && d.name === 'value2' && d.group === 2) {
        return color2(d.name)
      } else if (d.value == data.a_nh_overrall_ptile && d.name === 'value' && d.group === 1) {
        return color(d.name)
      } else {
        // '#3582D4',
        // '#246298' <== darker blue
        return texture.url()
      }
    })

  // Top Bar values
  label
    .selectAll('text')
    .data(function (d) {
      return d.group
    })
    .enter()
    .append('text')
    .attr('class', function (d) {
      if (isNaN(d.value)) {
        return 'context-overview-small-text'
      } else if (d.value == data.a_nh_overrall_ptile && d.name === 'value' && d.group === 1) {
        return 'context-overview-medium-text'
      } else if (d.value == data.a_hs_overrall_ptile && d.name === 'value2' && d.group === 2) {
        return 'context-overview-medium-text'
      } else {
        return 'context-overview-small-text'
      }
    })
    .attr('aria-hidden', 'true')
    .attr('x', function (d) {
      return x1(d.name) + x1.bandwidth() / 2
    })
    .attr('text-anchor', 'middle')
    .attr('transform', function (d) {
      if (d.value == getDIData.di_nh_avg && d.name === 'value2' && d.group === 1) {
        return `translate(${x1.bandwidth() / 3},-10)`
      } else if (d.value == data.a_nh_overrall_ptile && d.name === 'value' && d.group === 1) {
        return 'translate(0,-10)'
      } else if (d.value == data.a_hs_overrall_ptile && d.name === 'value2' && d.group === 2) {
        return 'translate(0,-10)'
      } else {
        return `translate(-${x1.bandwidth() / 3},-10)`
      }
    })
    .attr('y', function (d) {
      return getValue(d.value, isDecile) || 0
    })
    .text(function (d) {
      if (d.value === -1) {
        return 'NA'
      } else {
        if (d.value === 0) {
          return 'NA'
        } else {
          return isDecile ? switchToDecile(+d.value) : +d.value
        }
      }
    })
    .attr('font-weight', 900)

  // let dataL = 0
  // let offset = 100
  let legend = svg
    .selectAll('.legend')
    .data(groupNames.slice())
    .enter()
    .append('g')
    .attr('class', 'legend')
    .attr('aria-hidden', 'true')
    .attr('transform', function (i) {
      if (i === 0) {
        // let newdataL = dataL
        // dataL = d.length
        return 'translate(' + x1.bandwidth() / 6 + ',' + 125 + ')'
      } else {
        // let newdataL = dataL
        // dataL += d.length
        return 'translate(' + x1.bandwidth() * 4 + ',' + 125 + ')'
      }
    })
    .call(wrap, x0.bandwidth())

  let loop1 = 0
  let loop2 = 0
  let loop3 = 0

  let text = label
    .append('text')
    .attr('dx', 0)
    .attr('dy', '.9em')
    .attr('aria-hidden', 'true')
    .style('text-anchor', 'end')
    .attr('transform', function () {
      let dx = 0
      if (loop1 === 0) {
        ++loop1
        dx = x0.bandwidth() / 2 - x1.bandwidth() / 2 - 5
      } else {
        dx = x0.bandwidth() / 2 + x1.bandwidth() / 2 + 28
      }
      return 'translate(' + dx + ', -40)'
    })

  text
    .append('tspan')
    .text('Your')
    .attr('x', 0)
    .attr('y', function (d) {
      if (loop2 === 0) {
        ++loop2
        return (getValue(d.value2, isDecile) || 0) + 3
      } else {
        return (getValue(d.value, isDecile) || 0) + 3
      }
    })
    .attr('font-weight', 600)

  text
    .append('tspan')
    .text('College')
    .attr('x', function () {
      if (loop3 === 0) {
        ++loop3
        return 0
      } else {
        return 15
      }
    })
    .attr('dy', 17)
    .attr('font-weight', 600)
}

const wrap = (textList, width) => {
  textList.each(function () {
    const text = select(this)
    const tmp = text.text()
    const words = tmp.split(/\s+/)
    let line = []
    let lineNumber = 0
    const lineHeight = 1.1 // ems
    const y = text.attr('y')
    const dy = parseFloat(text.attr('dy') || 0) + 0.8
    const dx = tmp === 'Neighborhood Average' ? -(width / 3) : width / 3
    let tspan = text
      .text(null)
      .append('tspan')
      .attr('x', dx)
      .attr('y', y)
      .attr('dy', dy + 'em')
    words.forEach(function (word, index) {
      line.push(word)
      if (index > 0 && tspan.node().getComputedTextLength() > width) {
        line.pop()
        tspan.text(line.join(' '))
        line = [word]
        tspan = text
          .append('tspan')
          .attr('x', dx)
          .attr('y', y)
          .attr('dy', ++lineNumber * lineHeight + dy + 'em')
          .text(word)
          .style({ 'font-size': '13px' })
      } else {
        tspan.text(line.join(' '))
      }
    })
  })
}
